import { ORDER_VIEW_FILTERS, orderMatchesView } from './orderViewFilters';

export const ORDER_STATUS_FILTERS = {
	not_started: { name: 'No iniciada', args: { is_status_being_prepared: 0, is_status_prepared: 0, is_status_completed: 0 } },
	preparation_started: { name: 'Preparación iniciada', args: { is_status_prepared: 0, is_status_being_prepared: 1 } },
	prepared: { name: 'Preparada', args: { is_status_prepared: 1, is_provider_collected: 0 } },
	dispatched: { name: 'Despachada', args: { is_provider_collected: 1 }, implies: { is_status_prepared: 1 } },
	not_assigned: { name: 'No asignada', args: { is_shopper_assigned: 0, is_provider_assigned: 0, is_status_canceled: 0, is_status_expired: 0 } },
	assigned: { name: 'Asignada', args: { is_provider_assigned: 1, is_status_completed_ok: 0 } },
	started_by_provider: { name: 'Iniciada por repartidor', args: { is_provider_started: 1, is_status_completed_ok: 0 }, implies: { is_provider_assigned: 1 } },
	collected: { name: 'Recolectada', args: { is_provider_collected: 1, is_status_completed_ok: 0 } },
	completed: { name: 'Completada', args: { is_status_completed_ok: 1 } },
	cancelled: { name: 'Cancelada', args: { is_status_canceled: 1 } },
	in_progress: { name: 'En curso', args: { is_status_completed: 0, is_payment_confirmed: 1 } },
	payed: { name: 'Pagada', args: { is_payment_confirmed: 1 } },
	unpaid: { name: 'Sin pagar', args: { is_payment_confirmed: 0, is_status_completed: 0 }, archivedArgs: { is_payment_confirmed: 0 } },
};

export const COLUMN_STATUS_FILTERS = {
	branchStatus: ['not_started', 'preparation_started', 'prepared', 'dispatched', 'cancelled'],
	status: ['not_assigned', 'assigned', 'started_by_provider', 'collected', 'completed', 'cancelled'],
	status_payment: ['in_progress', 'payed', 'unpaid'],
};

const HIDDEN_COLUMN_FILTERS = {
	unassigned: ['status'],
};

export const PROVIDER_ROLE_LABELS = {
	deliverer: 'Repartidor',
	shopper: 'Shopper',
	both: 'Repartidor y shopper',
};

export const PROVIDER_SEARCH_MIN_LENGTH = 3;

export const PROVIDER_SEARCH_LIMIT = 30;

export const formatOrdersCount = (count) => `${count} ${count === 1 ? 'orden' : 'órdenes'}`;

const withImplications = (args) => {
	const out = { ...args };
	const fill = (key, value) => {
		if (out[key] === undefined) out[key] = value;
	};
	if (args.is_status_completed_ok === 1 || args.is_status_canceled === 1) fill('is_status_completed', 1);
	if (args.is_status_completed === 0) {
		fill('is_status_completed_ok', 0);
		fill('is_status_canceled', 0);
	}
	return out;
};

const optionFitsView = (option, viewArgs) => {
	const flags = withImplications({ ...option.args, ...option.implies });
	const conflicts = Object.keys(flags).some(key => viewArgs[key] !== undefined && viewArgs[key] !== flags[key]);
	const redundant = Object.keys(option.args).every(key => viewArgs[key] === option.args[key]);
	return !conflicts && !redundant;
};

const isUsedByOtherColumn = (value, column, columnFilters) =>
	Object.keys(COLUMN_STATUS_FILTERS).some(other => other !== column && columnFilters[other] === value);

export const getColumnFilterOptions = (column, orderType, columnFilters = {}) => {
	if ((HIDDEN_COLUMN_FILTERS[orderType] || []).includes(column)) return [];
	const viewArgs = withImplications(ORDER_VIEW_FILTERS[orderType] || {});
	return (COLUMN_STATUS_FILTERS[column] || [])
		.filter(key => optionFitsView(ORDER_STATUS_FILTERS[key], viewArgs))
		.filter(key => !isUsedByOtherColumn(key, column, columnFilters))
		.map(key => ({ value: key, name: ORDER_STATUS_FILTERS[key].name }));
};

export const isProviderFilterAvailable = (orderType) => orderType !== 'unassigned';

export const getDefaultColumnFilters = (orderType) => (orderType ? {} : { status_payment: 'in_progress' });

export const sanitizeColumnFilters = (filters = {}, orderType) => {
	const clean = {};
	Object.keys(COLUMN_STATUS_FILTERS).forEach((column) => {
		const value = filters[column];
		if (value && getColumnFilterOptions(column, orderType, filters).some(option => option.value === value)) {
			clean[column] = value;
		}
	});
	if (filters.provider && filters.provider.id && isProviderFilterAvailable(orderType)) {
		clean.provider = { id: filters.provider.id, name: filters.provider.name };
	}
	return clean;
};

const storageKey = (orderType) => `orderColumnFilters_${orderType || 'general'}`;

export const loadColumnFilters = (orderType) => {
	try {
		const stored = localStorage.getItem(storageKey(orderType));
		return stored ? sanitizeColumnFilters(JSON.parse(stored), orderType) : getDefaultColumnFilters(orderType);
	} catch (e) {
		return getDefaultColumnFilters(orderType);
	}
};

export const saveColumnFilters = (orderType, filters) => {
	try {
		localStorage.setItem(storageKey(orderType), JSON.stringify(filters));
	} catch (e) {}
};

export const getColumnFiltersArgs = (filters = {}, archived = false) =>
	Object.keys(COLUMN_STATUS_FILTERS).reduce((acc, column) => {
		const option = ORDER_STATUS_FILTERS[filters[column]];
		return option ? { ...acc, ...((archived && option.archivedArgs) || option.args) } : acc;
	}, {});

export const buildColumnFiltersQuery = (filters = {}, orderType, archived = false) => {
	const args = { ...getColumnFiltersArgs(filters, archived), ...(ORDER_VIEW_FILTERS[orderType] || {}) };
	if (filters.provider && filters.provider.id) {
		args['providers-id'] = filters.provider.id;
		args.assignment = 'any';
	}
	return Object.entries(args).map(([key, value]) => `${key}=${value}`).join('&');
};

let activeColumnFilters = {};

export const setActiveColumnFilters = (filters) => {
	activeColumnFilters = filters || {};
};

const flagsMatch = (order, args) =>
	Object.entries(args).every(([key, value]) => !(key in order) || Number(!!order[key]) === value);

const providerMatches = (order, provider) => {
	if (!provider || !provider.id) return true;
	const snapshot = order.order_provider_snapshot;
	return (snapshot && snapshot.provider && snapshot.provider.id === provider.id) || order.shopper_id === provider.id;
};

export const orderMatchesFilters = (order, orderType) =>
	orderMatchesView(order, orderType)
	&& flagsMatch(order, getColumnFiltersArgs(activeColumnFilters))
	&& providerMatches(order, activeColumnFilters.provider);



// WEBPACK FOOTER //
// ./src/utils/orderColumnFilters.js